import React, { memo, useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Typography, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useMarketplaceContext } from 'utils/context/MarketplaceContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import {
  useMembershipValidation,
  IAssociatedMessage,
} from 'apps/fleet-planner/hooks/useMembershipValidation'
import { IShipmentMotorCarrier, IShipmentDriver } from 'apps/fleet-planner/schema'
import { LoadAssignmentSchemaType } from 'apps/fleet-planner/schema/LoadAssignmentSchema'
import { IInlineEditProps, ILoad, ITodo } from 'apps/fleet-planner/types'
import { fetchCarriers } from 'apps/fleet-planner/utils/actions/carriers'
import { IUpdatedAssignmentProps } from 'apps/fleet-planner/utils/api'
import { findCarrierById } from 'apps/fleet-planner/utils/motorCarrierHelpers'
import { selectCarriersList } from 'apps/fleet-planner/utils/selectors'
import { InlineEditContainer, Card } from 'transfix-ui'
import { useRefresh } from 'utils/hooks'
import { ConfirmAssociationModal } from '../modals/ConfirmAssociationsModal'
import { LoadAssignmentForm } from './LoadAssignmentForm'
import { LoadAssignmentInlineDisplay } from './LoadAssignmentInlineDisplay'

const useLoadAssignmentInlineEditStyles = makeStyles({
  label: {
    color: brandColors.coolGray5,
  },
})

/**
 * Returns a request object that can be used with the API
 */
const getUpdateAssignmentRequest = ({
  drivers = [],
  dispatcherId,
  truckId,
  trailerId,
  motorCarrier,
  tractorVin,
}: Partial<LoadAssignmentSchemaType> = {}): IUpdatedAssignmentProps => {
  const update: IUpdatedAssignmentProps = {}

  const newDriverIds = drivers.map((d: Pick<IShipmentDriver, 'id'> | null) => {
    return d?.id ? d.id : null
  })

  if (newDriverIds) {
    update.driverIds = newDriverIds
  }

  if (dispatcherId) {
    update.dispatcherId = dispatcherId
  }

  if (truckId) {
    update.truckId = truckId
  } else {
    update.truckId = null
  }

  if (trailerId) {
    update.trailerId = trailerId
  } else {
    update.trailerId = null
  }

  if (motorCarrier?.id) {
    update.motorCarrierId = motorCarrier.id
  }

  if (tractorVin) {
    update.tractorVin = tractorVin
  }

  return update
}

interface ILoadAssignmentInlineEditProps extends IInlineEditProps<ILoad> {
  currentShipmentTodos?: ITodo
}

const NAME = `load-assignment`

export const LoadAssignmentInlineEdit = memo<ILoadAssignmentInlineEditProps>(
  ({
    currentEditField,
    currentShipmentTodos,
    dataTest,
    onEditModeRequested: onEditModeRequestedProp,
    value: valueProp,
  }) => {
    const carrierList = useSelector(selectCarriersList)
    const classes = useLoadAssignmentInlineEditStyles()
    const { getFlag } = useFeatureFlagContext()
    const { enabled: isFpCarrierPortalEnabled } = getFlag('fpCarrierPortal')
    const { isLoading, shipmentDetails, updateShipmentAssignment } = useMarketplaceContext()
    const { flags, info } = shipmentDetails || {}
    const { vin: tractorVin } = info || {}
    const { missingDriver, missingTruckNumber, missingVin } = currentShipmentTodos || {}
    const motorCarriers = useSelector(selectCarriersList)
    const dispatch = useDispatch()
    const { getUnassociatedItemsFromLoad, isAdminForCarrier } = useMembershipValidation()
    const [, setLocalChanges] = useState<Partial<ILoad>>({ ...valueProp })
    const { trackEvent } = useFMSAnalytics()

    const [errorMessage, setErrorMessage] = useState<string | undefined>()
    const [pendingRequest, setPendingRequest] = useState<LoadAssignmentSchemaType | undefined>()
    const [associatedMessages, setAssociatedMessages] = useState<IAssociatedMessage[]>([])
    const { id: shipmentId } = valueProp || {}

    /**
     * Fetch data on mount
     */
    useEffect(() => {
      if (!motorCarriers.length) {
        dispatch(fetchCarriers())
      }
    }, [dispatch, motorCarriers.length])

    // Update state when receiving new props
    useEffect(() => {
      setLocalChanges({ ...valueProp })
    }, [valueProp])

    const resetLocalChanges = () => {
      setLocalChanges({ ...valueProp })
    }

    /**
     * Triggers PUT update
     */
    const saveHandler = async (data: LoadAssignmentSchemaType) => {
      const requestData = getUpdateAssignmentRequest(data)

      if (valueProp?.id) {
        setErrorMessage(undefined)
        const res = await updateShipmentAssignment(valueProp?.id, requestData)
        const { id } = res || {}

        if (id) {
          trackEvent('Load', 'UPDATED_ASSIGNMENT_DETAILS', { fpLoadId: id, data })
          onEditModeRequestedProp('')

          // reset temporary data
          setPendingRequest(undefined)
          setAssociatedMessages([])
        } else {
          trackEvent('Load', 'ERROR_UPDATING_ASSIGNMENT_DETAILS', { fpLoadId: id, data, res })
          setErrorMessage('Could not update shipment. Please try again.')
        }
      }
    }

    /**
     * Submits form
     */
    const submitHandler = (data: LoadAssignmentSchemaType) => {
      // Only execute this `if` block when `isFpCarrierPortalEnabled` is true
      const unassociatedItems = getUnassociatedItemsFromLoad(data)
      const isAdmin = isAdminForCarrier(data.motorCarrierId)
      const hasWarnings = unassociatedItems.length

      // only show if admin user, otherwise errors are
      // displayed before this inline in form
      if (isFpCarrierPortalEnabled && isAdmin && hasWarnings) {
        setPendingRequest(data)
        setAssociatedMessages(unassociatedItems)
        return
      }

      saveHandler(data)
    }

    /**
     * Triggers PUT method for pending request
     * `pendingRequest` and `associatedMessages` will clear when the dispatch succeeds
     */
    const confirmHandler = () => {
      if (pendingRequest) {
        saveHandler(pendingRequest)
      }
    }

    const cancelModalHandler = useCallback(() => {
      // reset this data
      setPendingRequest(undefined)
      setAssociatedMessages([])
    }, [])

    const onCancel = () => {
      resetLocalChanges()
      onEditModeRequestedProp('')
    }

    const isEditMode = currentEditField === NAME

    const editModeRequestedHandler = (name?: string) => {
      if (!name) {
        resetLocalChanges()
      }
      onEditModeRequestedProp(name)
    }

    /**
     * Formats values from `valueProp` into a default values
     * object for assignment form
     */
    const getDefaultValues = useCallback(() => {
      const { dispatcherId, trailerId = '', truckId = '', drivers = [], motorCarrier } =
        valueProp || {}

      const values: Partial<LoadAssignmentSchemaType> = {}

      if (motorCarrier) {
        values.motorCarrier = motorCarrier
        values.motorCarrierId = motorCarrier.id
      }

      if (dispatcherId) {
        values.dispatcherId = dispatcherId
      }

      if (trailerId) {
        values.trailerId = `${trailerId}`
      }

      if (truckId) {
        values.truckId = `${truckId}`
      }

      /**
       * TODO: Fix this type-pocalypse
       */
      if (drivers.length)
        values.drivers = drivers.map(d => ({
          id: (d.id! as unknown) as number,
          mcNumber: (d.mcNumber as unknown) as number,
          transfixUserId: (d.transfixUserId as unknown) as number,
          name: d.name || '',
          motorCarriers: d.motorCarriers as IShipmentMotorCarrier[],
        }))

      if (tractorVin) {
        values.tractorVin = tractorVin
      }

      return values
    }, [tractorVin, valueProp])

    const valueWithVin = valueProp ? { ...valueProp, tractorVin } : undefined

    const { key: formKey, resetKey } = useRefresh()

    /**
     * When edit mode is toggled off, reset this form
     */
    useEffect(() => {
      if (!isEditMode) {
        resetKey()
        setErrorMessage(undefined)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditMode])

    // Used for displaying carrier name in modal
    const motorCarrier = pendingRequest?.motorCarrierId
      ? findCarrierById(carrierList, pendingRequest?.motorCarrierId)
      : undefined
    const { name: motorCarrierName } = motorCarrier || {}

    return (
      <>
        <InlineEditContainer
          isLoading={isLoading}
          dataTest={dataTest}
          className='relative mb3 load-assignment'
          name={NAME}
          editModeEnabled={isEditMode}
          editIconLocation='top'
          resetOnClickOutside={!isLoading && !pendingRequest}
          editMode={
            <Card className='pa4'>
              <LoadAssignmentForm
                key={formKey}
                isLoading={isLoading}
                defaultValues={{
                  ...getDefaultValues(),
                }}
                onCancel={onCancel}
                onSave={submitHandler}
                error={errorMessage}
                shipmentId={shipmentId}
                shipmentFlags={flags}
              />
            </Card>
          }
          onEditModeRequested={editModeRequestedHandler}>
          <LoadAssignmentInlineDisplay
            value={valueWithVin}
            missingDriver={missingDriver}
            missingTruckNumber={missingTruckNumber}
            missingVin={missingVin}
          />
        </InlineEditContainer>
        <ConfirmAssociationModal
          isLoading={isLoading}
          open={!!associatedMessages.length}
          onConfirm={confirmHandler}
          onCancel={cancelModalHandler}>
          <Box mb={-2}>
            <Box mb={2}>
              <Typography variant='subtitle2' className={classes.label}>
                Carrier
              </Typography>
              <Typography variant='body2'>{motorCarrierName}</Typography>
            </Box>
            {associatedMessages.map(item => {
              const { name, value } = item || {}
              return (
                <Box
                  key={name}
                  mb={associatedMessages.indexOf(item) !== associatedMessages.length - 1 ? 2 : 0}>
                  <Typography variant='subtitle2' className={classes.label}>
                    {name}
                  </Typography>
                  <Typography variant='body2'>{value}</Typography>
                </Box>
              )
            })}
          </Box>
        </ConfirmAssociationModal>
      </>
    )
  }
)
